import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { CheckboxGroups } from '~/eds/src/components/CheckboxGroups/CheckboxGroups';
import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { SearchInput } from '~/eds/src/components/SearchInput/SearchInput';
import { Section } from '~/eds/src/components/Section/Section';
import { TextArea } from '~/eds/src/components/TextArea/TextArea';
import { TextInput } from '~/eds/src/components/TextInput/TextInput';
import { useFormatNumber } from '~/i18n/hooks/useFormatNumber/useFormatNumber';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';

import { WEBHOOKS_DESCRIPTION_LENGTH } from '../constants';
import { checkActiveEndpointEvents, checkValidUrl } from '../utils';

export const EndpointConfiguration = () => {
  const intl = useIntl();
  const formatNumber = useFormatNumber();
  const dispatch = useDispatch();
  const [search, setSearch] = useState<string>('');

  const { activeEndpoint, configuration } = useSelector(
    slices.webhooks.selectors.selectEndpointConfiguration,
  );
  const { url, description, events } = configuration;

  const { data: eventGroups = [], isLoading: isLoadingEvents } =
    api.endpoints.getConfigurationEvents.useQuery(undefined);

  useEffect(() => {
    if (activeEndpoint && !isLoadingEvents) {
      dispatch(
        slices.webhooks.actions.setConfiguration({
          url: activeEndpoint.endpointUrl,
          description: activeEndpoint.description,
          events: checkActiveEndpointEvents({ activeEndpoint, eventGroups }),
        }),
      );
    }
  }, [activeEndpoint, eventGroups, isLoadingEvents]);

  return (
    <Section
      title={intl.formatMessage({
        id: 'EndpointConfiguration.sectionTitle',
        defaultMessage: 'Listen to webhooks and events',
        description: 'Section title for endpoint configuration',
      })}
      description={intl.formatMessage({
        id: 'EndpointConfiguration.sectionDescription',
        defaultMessage:
          'Create webhooks and endpoints to help with your integration events.',
        description: 'Section description for endpoint configuration',
      })}
    >
      <FormField<string, false>
        error={checkValidUrl(url, intl)}
        input={TextInput}
        inputProps={{
          placeholder: 'https://…',
        }}
        label={intl.formatMessage({
          id: 'EndpointConfiguration.urlLabel',
          defaultMessage: 'Endpoint URL',
          description: 'Label for endpoint URL field',
        })}
        name="endpoint url"
        onChange={(url) => {
          dispatch(slices.webhooks.actions.setUrl(url ?? ''));
        }}
        required
        value={url}
      />
      <FormField<string, false>
        footer={`${formatNumber(description?.length ?? 0)}/${formatNumber(
          WEBHOOKS_DESCRIPTION_LENGTH,
          {
            unit: intl.formatMessage({
              id: 'EndpointConfiguration.characterUnit',
              defaultMessage: 'characters',
              description: 'Unit for character count in description',
            }),
          },
        )}`}
        input={TextArea}
        inputProps={{
          maxLength: WEBHOOKS_DESCRIPTION_LENGTH,
          placeholder: intl.formatMessage({
            id: 'EndpointConfiguration.descriptionPlaceholder',
            defaultMessage:
              'Add an optional description for what this endpoint is used for.',
            description: 'Placeholder for description field',
          }),
        }}
        label={intl.formatMessage({
          id: 'EndpointConfiguration.descriptionLabel',
          defaultMessage: 'Description',
          description: 'Label for description field',
        })}
        name="description"
        onChange={(description) => {
          dispatch(slices.webhooks.actions.setDescription(description ?? ''));
        }}
        optional
        value={description ?? ''}
      />
      <FormField<string, false>
        input={SearchInput}
        description={intl.formatMessage({
          id: 'EndpointConfiguration.eventsDescription',
          defaultMessage:
            'You must select at least one event in order to create an endpoint.',
          description: 'Description for events selection field',
        })}
        inputProps={{
          placeholder: intl.formatMessage({
            id: 'EndpointConfiguration.searchPlaceholder',
            defaultMessage: 'Search',
            description: 'Placeholder for search input',
          }),
        }}
        label={intl.formatMessage({
          id: 'EndpointConfiguration.eventsLabel',
          defaultMessage: 'Select events',
          description: 'Label for events selection field',
        })}
        name="events"
        onChange={(search) => {
          setSearch(search ?? '');
        }}
        value={search ?? ''}
      />
      <ContentContainer loadingContent={{ isLoading: isLoadingEvents }}>
        <CheckboxGroups
          collapsible
          mode="descriptive"
          enableCount
          enableSelectAll={{
            label: intl.formatMessage({
              id: 'EndpointConfiguration.selectAllLabel',
              defaultMessage: 'Select all',
              description: 'Label for select all checkbox',
            }),
            name: 'select all',
            info: intl.formatMessage({
              id: 'EndpointConfiguration.selectAllInfo',
              defaultMessage:
                "Your endpoint will listen to all existing event types but won't automatically handle any new ones added later.",
              description: 'Info text for select all checkbox',
            }),
          }}
          groups={eventGroups}
          onChange={(events) => {
            dispatch(slices.webhooks.actions.setEvents(events ?? {}));
          }}
          search={search ?? ''}
          value={events}
          onOptionClick={(option) => {
            dispatch(slices.webhooks.actions.setSampleEvent(option.value));
          }}
        />
      </ContentContainer>
    </Section>
  );
};
